import React, { useState, ReactElement, useEffect, SyntheticEvent } from 'react';
import { indemnityPeriodOptions } from '../../../types';
import Dropdown from '../../../../../components/Dropdown';
import SectionLabel from '../../../../../components/SectionLabel';
import { Grid, Icon, Input, InputOnChangeData } from 'semantic-ui-react';
import {
  FormikErrorMessage,
  DivErrorMessage
} from '../../../../../components/ErrorMessage';
import { Field } from 'formik';
import { formatAmount, unmaskMoney, maskCpfOrCnpj } from '../../../../../services/masks';
import { getErrorFormik } from '../../../../../services/errors';
import { FieldLabel } from '../../../../../styles';
import { StepProps } from '../../../BondInsuranceForm';
import { round } from '../../../../../util';
import Toggle from '../../../../../components/Toggle';
import styled from 'styled-components';
import { iRootDispatch, iRootState } from '../../../../../store';
import { BondInsuranceFormState } from '../../../../../store/types/temp-types';
import { connect } from 'react-redux';
import { FieldLabelContainer, ResponsiveFieldColumn, SpacedColumn } from '../../../style';
import IconButton from '../../../../../components/IconButton';

const SECTION_NAME = 'property_info';

const mapState = (state: iRootState) => ({
  bondInsuranceForm: state.bondInsuranceForm
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateBondInsurance: (bondInsuranceFormState: BondInsuranceFormState) =>
    dispatch.bondInsuranceForm.updateBondInsurance(bondInsuranceFormState)
});

type PropertyDataProps = StepProps &
  ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const PropertyData = (props: PropertyDataProps): ReactElement => {
  const { errors, touched, setFieldValue, handleInputChange, setFieldTouched, values } =
    props;

  const { property_info } = props.values;

  const {
    rent_value,
    condominium_fee,
    taxes_value,
    energy_bill,
    water_bill,
    gas_bill,
    contract_penalties_value,
    interior_painting_value,
    exterior_painting_value,
    furniture_damage_value,
    property_damage_value
  } = property_info;

  const [isDocValid, setIsDocValid] = useState<boolean>(true);
  const [isContractPenaltiesCoverageChecked, setIsContractPenaltiesCoverageChecked] =
    useState<boolean>(false);
  const [isInteriorPaintingCoverageChecked, setIsInteriorPaintingCoverageChecked] =
    useState<boolean>(false);
  const [isExteriorPaintingCoverageChecked, setIsExteriorPaintingCoverageChecked] =
    useState<boolean>(false);
  const [isFurnitureDamageCoverageChecked, setIsFurnitureDamageCoverageChecked] =
    useState<boolean>(false);
  const [isPropertyDamageCoverageChecked, setIsPropertyDamageCoverageChecked] =
    useState<boolean>(false);

  useEffect(() => {
    setFieldValue(`${SECTION_NAME}.total_amount`, 0);
  }, []);

  useEffect(() => {
    props.bondInsuranceForm.isToo
      ? setFieldValue(
          `${SECTION_NAME}.total_amount`,
          unmaskMoney(rent_value || '0') +
            unmaskMoney(condominium_fee || '0') +
            unmaskMoney(taxes_value || '0') +
            unmaskMoney(energy_bill || '0') +
            unmaskMoney(water_bill || '0') +
            unmaskMoney(gas_bill || '0') +
            unmaskMoney(contract_penalties_value || '0') +
            unmaskMoney(interior_painting_value || '0') +
            unmaskMoney(exterior_painting_value || '0') +
            unmaskMoney(furniture_damage_value || '0') +
            unmaskMoney(property_damage_value || '0')
        )
      : setFieldValue(
          `${SECTION_NAME}.total_amount`,
          unmaskMoney(rent_value || '0') +
            unmaskMoney(condominium_fee || '0') +
            unmaskMoney(taxes_value || '0') +
            unmaskMoney(energy_bill || '0') +
            unmaskMoney(water_bill || '0') +
            unmaskMoney(gas_bill || '0')
        );
  }, [
    rent_value,
    condominium_fee,
    taxes_value,
    energy_bill,
    water_bill,
    gas_bill,
    contract_penalties_value,
    interior_painting_value,
    exterior_painting_value,
    furniture_damage_value,
    property_damage_value,
    props.bondInsuranceForm.isToo,
    setFieldValue
  ]);

  const validateErros = (field: string) =>
    getErrorFormik(errors, touched, SECTION_NAME, field);

  return (
    <>
      <SectionLabel text="DADOS DO IMÓVEL" />
      <Grid.Row
        style={{
          marginBottom: '0'
        }}
      >
        <Grid.Column
          style={{
            textAlign: 'center'
          }}
        >
          <ChargesDisclaimer>
            <Icon bordered name="exclamation" />
            <div>
              <p>
                <b>Importante -</b> Atente-se ao preenchimento dos campos abaixo para fins
                de cobertura.
              </p>
              <p>Os campos não preenchidos serão desconsiderados em caso de sinistro.</p>
            </div>
          </ChargesDisclaimer>
        </Grid.Column>
      </Grid.Row>

      {property_info.reason === 'Locação para moradia' && (
        <>
          <Grid.Row
            style={{
              marginTop: '0'
            }}
          >
            <SpacedColumn mobile={16} tablet={8} computer={8}>
              <FieldLabel>NOME DO REAL OCUPANTE*</FieldLabel>
              <Field
                id={`${SECTION_NAME}.real_occupant_name`}
                name={`${SECTION_NAME}.real_occupant_name`}
                value={values.property_info.real_occupant_name}
                onChange={handleInputChange}
                onBlur={() => setFieldTouched(`${SECTION_NAME}.real_occupant_name`, true)}
                error={validateErros('real_occupant_name')}
                component={Input}
                fluid
              />
              <FormikErrorMessage
                component="div"
                name={`${SECTION_NAME}.real_occupant_name`}
              />
            </SpacedColumn>

            <SpacedColumn mobile={16} tablet={8} computer={8}>
              <FieldLabel>CPF DO REAL OCUPANTE*</FieldLabel>
              <Field
                id={`${SECTION_NAME}.real_occupant_doc`}
                name={`${SECTION_NAME}.real_occupant_doc`}
                value={values.property_info.real_occupant_doc}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement>,
                  data: InputOnChangeData
                ): void => {
                  data.value = maskCpfOrCnpj(data.value);
                  handleInputChange(e, data);
                }}
                onBlur={() => setFieldTouched(`${SECTION_NAME}.real_occupant_doc`, true)}
                error={validateErros('real_occupant_doc')}
                component={Input}
                fluid
              />
              {isDocValid ? null : <DivErrorMessage>Documento inválido</DivErrorMessage>}
              <FormikErrorMessage
                component="div"
                name={`${SECTION_NAME}.real_occupant_doc`}
              />
            </SpacedColumn>
          </Grid.Row>
        </>
      )}

      <Grid.Row style={{ marginTop: '0' }}>
        <Grid.Column width={16}>
          <ChargesGrid>
            <ChargeField>
              <ChargeFieldLabel>
                Valor do aluguel <RequiredMark>*</RequiredMark>
              </ChargeFieldLabel>
              <Field
                data-ci={`${SECTION_NAME}.rent_value`}
                id={`${SECTION_NAME}.rent_value`}
                name={`${SECTION_NAME}.rent_value`}
                value={property_info.rent_value}
                component={Input}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement>,
                  data: InputOnChangeData
                ): void => {
                  data.value = formatAmount(data.value);
                  handleInputChange(e, data);

                  if (props.bondInsuranceForm.isToo) {
                    if (isContractPenaltiesCoverageChecked) {
                      setFieldValue(
                        `${SECTION_NAME}.contract_penalties_value`,
                        data.value
                      );
                    }
                    if (isInteriorPaintingCoverageChecked) {
                      setFieldValue(
                        `${SECTION_NAME}.interior_painting_value`,
                        data.value
                      );
                    }
                    if (isExteriorPaintingCoverageChecked) {
                      setFieldValue(
                        `${SECTION_NAME}.exterior_painting_value`,
                        data.value
                      );
                    }
                    if (isFurnitureDamageCoverageChecked) {
                      setFieldValue(`${SECTION_NAME}.furniture_damage_value`, data.value);
                    }
                    if (isPropertyDamageCoverageChecked) {
                      setFieldValue(`${SECTION_NAME}.property_damage_value`, data.value);
                    }
                  }
                }}
                onBlur={() => {
                  setFieldTouched(`${SECTION_NAME}.rent_value`, true);
                }}
                error={validateErros('rent_value')}
                fluid
              />
              <FormikErrorMessage component="div" name={`${SECTION_NAME}.rent_value`} />
            </ChargeField>

            <ChargeField>
              <ChargeFieldLabel>
                Valor do condomínio
                {property_info.is_condominium_fee_required && (
                  <RequiredMark>*</RequiredMark>
                )}
              </ChargeFieldLabel>
              <FieldLabelContainer>
                <Field
                  id={`${SECTION_NAME}.condominium_fee`}
                  name={`${SECTION_NAME}.condominium_fee`}
                  value={property_info.condominium_fee}
                  component={Input}
                  disabled={!property_info.is_condominium_fee_required}
                  placeholder="0,00"
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    data: InputOnChangeData
                  ): void => {
                    data.value = formatAmount(data.value);
                    handleInputChange(e, data);
                  }}
                  onBlur={() => {
                    setFieldTouched(`${SECTION_NAME}.condominium_fee`, true);
                  }}
                  error={
                    property_info.is_condominium_fee_required &&
                    validateErros('condominium_fee')
                  }
                  fluid
                />
                <IconButton
                  className="icon-button"
                  onClick={(e: SyntheticEvent) => {
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.is_condominium_fee_required`
                      },
                      value: !property_info.is_condominium_fee_required
                    });
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.condominium_fee`
                      },
                      value: '0,00'
                    });
                  }}
                >
                  <Icon
                    name="ban"
                    className={
                      !property_info.is_condominium_fee_required ? 'not-required' : ''
                    }
                  />
                </IconButton>
              </FieldLabelContainer>
              {property_info.is_condominium_fee_required && (
                <FormikErrorMessage
                  component="div"
                  name={`${SECTION_NAME}.condominium_fee`}
                />
              )}
            </ChargeField>

            <ChargeField>
              <ChargeFieldLabel>
                Valor do IPTU mensal
                {property_info.is_taxes_value_required && <RequiredMark>*</RequiredMark>}
              </ChargeFieldLabel>
              <FieldLabelContainer>
                <Field
                  id={`${SECTION_NAME}.taxes_value`}
                  name={`${SECTION_NAME}.taxes_value`}
                  value={property_info.taxes_value}
                  component={Input}
                  disabled={!property_info.is_taxes_value_required}
                  placeholder="0,00"
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    data: InputOnChangeData
                  ): void => {
                    data.value = formatAmount(data.value);
                    handleInputChange(e, data);
                  }}
                  onBlur={() => {
                    setFieldTouched(`${SECTION_NAME}.taxes_value`, true);
                  }}
                  error={
                    property_info.is_taxes_value_required && validateErros('taxes_value')
                  }
                  fluid
                />
                <IconButton
                  className="icon-button"
                  onClick={(e: SyntheticEvent) => {
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.is_taxes_value_required`
                      },
                      value: !property_info.is_taxes_value_required
                    });

                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.taxes_value`
                      },
                      value: '0,00'
                    });
                  }}
                >
                  <Icon
                    name="ban"
                    className={
                      !property_info.is_taxes_value_required ? 'not-required' : ''
                    }
                  />
                </IconButton>
              </FieldLabelContainer>
              {property_info.is_taxes_value_required && (
                <FormikErrorMessage
                  component="div"
                  name={`${SECTION_NAME}.taxes_value`}
                />
              )}
            </ChargeField>

            <ChargeField>
              <ChargeFieldLabel>
                Valor da energia
                {property_info.is_energy_bill_required && <RequiredMark>*</RequiredMark>}
              </ChargeFieldLabel>
              <FieldLabelContainer>
                <Field
                  id={`${SECTION_NAME}.energy_bill`}
                  name={`${SECTION_NAME}.energy_bill`}
                  value={property_info.energy_bill}
                  disabled={!property_info.is_energy_bill_required}
                  placeholder="0,00"
                  component={Input}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    data: InputOnChangeData
                  ): void => {
                    data.value = formatAmount(data.value);
                    handleInputChange(e, data);
                  }}
                  onBlur={() => {
                    setFieldTouched(`${SECTION_NAME}.energy_bill`, true);
                  }}
                  error={
                    property_info.is_energy_bill_required && validateErros('energy_bill')
                  }
                  fluid
                />
                <IconButton
                  className="icon-button"
                  onClick={(e: SyntheticEvent) => {
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.is_energy_bill_required`
                      },
                      value: !property_info.is_energy_bill_required
                    });
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.energy_bill`
                      },
                      value: '0,00'
                    });
                  }}
                >
                  <Icon
                    name="ban"
                    className={
                      !property_info.is_energy_bill_required ? 'not-required' : ''
                    }
                  />
                </IconButton>
              </FieldLabelContainer>
              {property_info.is_energy_bill_required && (
                <FormikErrorMessage
                  component="div"
                  name={`${SECTION_NAME}.energy_bill`}
                />
              )}
            </ChargeField>

            <ChargeField>
              <ChargeFieldLabel>
                Valor da água
                {property_info.is_water_bill_required && <RequiredMark>*</RequiredMark>}
              </ChargeFieldLabel>
              <FieldLabelContainer>
                <Field
                  id={`${SECTION_NAME}.water_bill`}
                  name={`${SECTION_NAME}.water_bill`}
                  value={property_info.water_bill}
                  disabled={!property_info.is_water_bill_required}
                  placeholder="0,00"
                  component={Input}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    data: InputOnChangeData
                  ): void => {
                    data.value = formatAmount(data.value);
                    handleInputChange(e, data);
                  }}
                  onBlur={() => {
                    setFieldTouched(`${SECTION_NAME}.water_bill`, true);
                  }}
                  error={validateErros('water_bill')}
                  fluid
                />
                <IconButton
                  className="icon-button"
                  onClick={(e: SyntheticEvent) => {
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.is_water_bill_required`
                      },
                      value: !property_info.is_water_bill_required
                    });

                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.water_bill`
                      },
                      value: '0,00'
                    });
                  }}
                >
                  <Icon
                    name="ban"
                    className={
                      !property_info.is_water_bill_required ? 'not-required' : ''
                    }
                  />
                </IconButton>
              </FieldLabelContainer>
              {property_info.is_water_bill_required && (
                <FormikErrorMessage component="div" name={`${SECTION_NAME}.water_bill`} />
              )}
            </ChargeField>

            <ChargeField>
              <ChargeFieldLabel>
                Valor do gás canalizado
                {property_info.is_gas_bill_required && <RequiredMark>*</RequiredMark>}
              </ChargeFieldLabel>
              <FieldLabelContainer>
                <Field
                  id={`${SECTION_NAME}.gas_bill`}
                  name={`${SECTION_NAME}.gas_bill`}
                  value={property_info.gas_bill}
                  disabled={!property_info.is_gas_bill_required}
                  placeholder="0,00"
                  component={Input}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement>,
                    data: InputOnChangeData
                  ): void => {
                    data.value = formatAmount(data.value);
                    handleInputChange(e, data);
                  }}
                  onBlur={() => {
                    setFieldTouched(`${SECTION_NAME}.gas_bill`, true);
                  }}
                  error={property_info.is_gas_bill_required && validateErros('gas_bill')}
                  fluid
                />
                <IconButton
                  className="icon-button"
                  onClick={(e: SyntheticEvent) => {
                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.is_gas_bill_required`
                      },
                      value: !property_info.is_gas_bill_required
                    });

                    handleInputChange(e, {
                      field: {
                        name: `${SECTION_NAME}.gas_bill`
                      },
                      value: '0,00'
                    });
                  }}
                >
                  <Icon
                    name="ban"
                    className={!property_info.is_gas_bill_required ? 'not-required' : ''}
                  />
                </IconButton>
              </FieldLabelContainer>
              {property_info.is_gas_bill_required && (
                <FormikErrorMessage component="div" name={`${SECTION_NAME}.gas_bill`} />
              )}
            </ChargeField>

            <TotalChargeField>
              <ChargeFieldLabel>Valor total</ChargeFieldLabel>
              <TotalChargeValue>
                {formatAmount(
                  round((property_info.total_amount || 0) * 100, 2).toString()
                )}
              </TotalChargeValue>
            </TotalChargeField>
          </ChargesGrid>

          <ChargesWarning>
            <Icon name="exclamation circle" />
            <span>
              Para prosseguir é necessário informar todos os valores da locação. Caso não
              haja valores a declarar, bloqueie os campos clicando em{' '}
              <Icon name="arrow right" />
              <Icon name="ban" />
            </span>
          </ChargesWarning>
        </Grid.Column>
      </Grid.Row>

      {props.bondInsuranceForm.isToo && (
        <>
          <Grid.Row>
            <ResponsiveFieldColumn width={4}>
              <OptionContainer>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel>MULTAS CONTRATUAIS*</FieldLabel>
                    <Field
                      id={`contract_penalties_value`}
                      name={`${SECTION_NAME}.contract_penalties_value`}
                      value={property_info.contract_penalties_value}
                      component={Input}
                      onBlur={() => {
                        setFieldTouched(`${SECTION_NAME}.contract_penalties_value`, true);
                      }}
                      disabled={true}
                      error={validateErros('contract_penalties_value')}
                      fluid
                    />
                    <FormikErrorMessage
                      component="div"
                      name={`${SECTION_NAME}.contract_penalties_value`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Toggle
                  id={`is_contract_penalties_coverage_checked`}
                  name={`${SECTION_NAME}.is_contract_penalties_coverage_checked`}
                  color="blue"
                  style={{ alignSelf: 'center' }}
                  onChange={(e: SyntheticEvent, data: any) => {
                    if (data) {
                      setIsContractPenaltiesCoverageChecked(data.checked);

                      if (data.checked) {
                        setFieldValue(
                          `${SECTION_NAME}.contract_penalties_value`,
                          values.property_info.rent_value
                        );
                      } else {
                        setFieldValue(`${SECTION_NAME}.contract_penalties_value`, '0,00');
                      }
                    }
                  }}
                  checked={isContractPenaltiesCoverageChecked}
                  disabled={true}
                />
              </OptionContainer>
            </ResponsiveFieldColumn>

            <ResponsiveFieldColumn width={4}>
              <OptionContainer>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel>PINTURA INTERNA</FieldLabel>
                    <Field
                      id={`interior_painting_value`}
                      name={`${SECTION_NAME}.interior_painting_value`}
                      value={property_info.interior_painting_value}
                      component={Input}
                      onBlur={() => {
                        setFieldTouched(`${SECTION_NAME}.interior_painting_value`, true);
                      }}
                      disabled={true}
                      error={validateErros('interior_painting_value')}
                      fluid
                    />
                    <FormikErrorMessage
                      component="div"
                      name={`${SECTION_NAME}.interior_painting_value`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Toggle
                  id={`is_interior_painting_coverage_checked`}
                  name={`${SECTION_NAME}.is_interior_painting_coverage_checked`}
                  color="blue"
                  style={{ alignSelf: 'center' }}
                  onChange={(e: SyntheticEvent, data: any) => {
                    if (data) {
                      setIsInteriorPaintingCoverageChecked(data.checked);

                      if (data.checked) {
                        setFieldValue(
                          `${SECTION_NAME}.interior_painting_value`,
                          values.property_info.rent_value
                        );
                      } else {
                        setFieldValue(`${SECTION_NAME}.interior_painting_value`, '0,00');
                      }
                    }
                  }}
                  checked={isInteriorPaintingCoverageChecked}
                  disabled={false}
                />
              </OptionContainer>
            </ResponsiveFieldColumn>

            <ResponsiveFieldColumn width={4}>
              <OptionContainer>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel>PINTURA EXTERNA*</FieldLabel>
                    <Field
                      id={`exterior_painting_value`}
                      name={`${SECTION_NAME}.exterior_painting_value`}
                      value={property_info.exterior_painting_value}
                      component={Input}
                      onBlur={() => {
                        setFieldTouched(`${SECTION_NAME}.exterior_painting_value`, true);
                      }}
                      disabled={true}
                      error={validateErros('exterior_painting_value')}
                      fluid
                    />
                    <FormikErrorMessage
                      component="div"
                      name={`${SECTION_NAME}.exterior_painting_value`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Toggle
                  id={`is_exterior_painting_coverage_checked`}
                  name={`${SECTION_NAME}.is_exterior_painting_coverage_checked`}
                  color="blue"
                  style={{ alignSelf: 'center' }}
                  onChange={(e: SyntheticEvent, data: any) => {
                    if (data) {
                      setIsExteriorPaintingCoverageChecked(data.checked);

                      if (data.checked) {
                        setFieldValue(
                          `${SECTION_NAME}.exterior_painting_value`,
                          values.property_info.rent_value
                        );
                      } else {
                        setFieldValue(`${SECTION_NAME}.exterior_painting_value`, '0,00');
                      }
                    }
                  }}
                  checked={isExteriorPaintingCoverageChecked}
                  disabled={true}
                />
              </OptionContainer>
            </ResponsiveFieldColumn>

            <ResponsiveFieldColumn width={4}>
              <OptionContainer>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel>DANOS A MÓVEIS</FieldLabel>
                    <Field
                      id={`furniture_damage_value`}
                      name={`${SECTION_NAME}.furniture_damage_value`}
                      value={property_info.furniture_damage_value}
                      component={Input}
                      onBlur={() => {
                        setFieldTouched(`${SECTION_NAME}.furniture_damage_value`, true);
                      }}
                      disabled={true}
                      error={validateErros('furniture_damage_value')}
                      fluid
                    />
                    <FormikErrorMessage
                      component="div"
                      name={`${SECTION_NAME}.furniture_damage_value`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Toggle
                  id={`is_furniture_damage_coverage_checked`}
                  name={`${SECTION_NAME}.is_furniture_damage_coverage_checked`}
                  color="blue"
                  style={{ alignSelf: 'center' }}
                  onChange={(e: SyntheticEvent, data: any) => {
                    if (data) {
                      setIsFurnitureDamageCoverageChecked(data.checked);

                      if (data.checked) {
                        setFieldValue(
                          `${SECTION_NAME}.furniture_damage_value`,
                          values.property_info.rent_value
                        );
                      } else {
                        setFieldValue(`${SECTION_NAME}.furniture_damage_value`, '0,00');
                      }
                    }
                  }}
                  checked={isFurnitureDamageCoverageChecked}
                  disabled={false}
                />
              </OptionContainer>
            </ResponsiveFieldColumn>
          </Grid.Row>

          <Grid.Row>
            <ResponsiveFieldColumn width={4}>
              <OptionContainer>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <FieldLabel>DANOS AO IMÓVEL</FieldLabel>
                    <Field
                      id={`property_damage_value`}
                      name={`${SECTION_NAME}.property_damage_value`}
                      value={property_info.property_damage_value}
                      component={Input}
                      onBlur={() => {
                        setFieldTouched(`${SECTION_NAME}.property_damage_value`, true);
                      }}
                      disabled={true}
                      error={validateErros('property_damage_value')}
                      fluid
                    />
                    <FormikErrorMessage
                      component="div"
                      name={`${SECTION_NAME}.property_damage_value`}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Toggle
                  id={`is_property_damage_coverage_checked`}
                  name={`${SECTION_NAME}.is_property_damage_coverage_checked`}
                  color="blue"
                  style={{ alignSelf: 'center' }}
                  onChange={(e: SyntheticEvent, data: any) => {
                    if (data) {
                      setIsPropertyDamageCoverageChecked(data.checked);

                      if (data.checked) {
                        setFieldValue(
                          `${SECTION_NAME}.property_damage_value`,
                          values.property_info.rent_value
                        );
                      } else {
                        setFieldValue(`${SECTION_NAME}.property_damage_value`, '0,00');
                      }
                    }
                  }}
                  checked={isPropertyDamageCoverageChecked}
                  disabled={false}
                />
              </OptionContainer>
            </ResponsiveFieldColumn>
          </Grid.Row>
        </>
      )}
    </>
  );
};

const ChargesDisclaimer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: center;
  align-items: center;
  min-height: 50px;
  padding: 8px 18px;
  border: 1px solid ${props => props.theme.secundary.color};
  border-radius: 8px;
  text-align: left;

  p {
    margin: 0;
    font-size: 12px;
    line-height: 16px;
  }

  p + p {
    position: relative;
    margin-left: 10px;

    &:before {
      content: '•';
      position: absolute;
      left: -8px;
    }
  }

  i.bordered.icon {
    background: ${props => props.theme.main.color};
    color: white;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(89, 36, 155, 0.28);
  }

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const ChargesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 18px;
  align-items: start;
  margin-top: 10px;

  .ui.input input {
    height: 38px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
`;

const ChargeField = styled.div`
  min-width: 0;
`;

const ChargeFieldLabel = styled.div`
  font-weight: bold;
  cursor: default;
  min-height: 18px;
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 16px;
  text-transform: none;
`;

const RequiredMark = styled.span`
  color: #ff4d00;
  margin-left: 2px;
`;

const TotalChargeField = styled.div`
  min-width: 0;
  padding-top: 1px;
`;

const TotalChargeValue = styled.div`
  color: ${props => props.theme.main.color};
  font-size: 13px;
  font-weight: bold;
  line-height: 38px;
`;

const ChargesWarning = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  max-width: 100%;
  color: #ff4d00;
  font-size: 12px;
  line-height: 16px;
  background-color: #e550001a;
  padding: 6px 8px;
  border-radius: 4px;

  i.icon {
    margin: 0;
  }

  > i:first-child {
    font-size: 18px;
  }
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5em 0;
  width: 100%;
  border: 1px solid silver;
  border-radius: 5px;
  padding: 2.5%;
`;

export default connect(mapState, mapDispatch)(PropertyData);
